<script setup>
import { ref, nextTick, watch, inject } from 'vue'
import LineChartOptions from "@/components/explorer/visualizations/line/LineChartOptions.vue"
import BarChartOptions from "@/components/explorer/visualizations/bar/BarChartOptions.vue"
import MapOptions from "@/components/explorer/visualizations/map/MapOptions.vue"
import TreemapChartOptions from "@/components/explorer/visualizations/treemap/TreemapChartOptions.vue"
import SankeyChartOptions from "@/components/explorer/visualizations/sankey/SankeyChartOptions.vue"
import SourceComponent from "@/components/explorer/visualizations/source/SourceComponent.vue"
import NoDataMsg from "@/components/common/NoDataMsg.vue"
import SkeletonLoader from "@/components/explorer/visualizations/SkeletonLoader.vue"
import SettingsSource from "@/components/explorer/visualizations/SettingsSource.vue";

import {defineChartProps as defineLineChartProps, refreshLineChart} from './visualizations/line/LineChartSetup';
import {defineChartProps as defineBarChartProps, refreshBarChart} from './visualizations/bar/BarChartSetup';
import {defineChartProps as defineTreemapChartProps, refreshTreemapChart} from './visualizations/treemap/TreemapChartSetup';
import {defineChartProps as defineSankeyChartProps, refreshSankeyChart} from './visualizations/sankey/SankeyChartSetup';
//import {setIndicatorSourceNotes}  from './visualizations/source/SourceSetup';
//import {setTableProps as setTableProps} from './visualizations/table/GridTableSetup';
import GridTable from "@/components/explorer/visualizations/table/GridTableComponent.vue";
import MapComponent from "@/components/explorer/visualizations/map/MapComponent.vue";
import {ChartTypeEnum} from  "@/components/explorer/visualizations/ChartTypeEnum.js"

import {getLineChartSettings, updateLineChartSettings, rebuildLineChartSettings}  from './visualizations/line/LineChartSetup';
import {getBarChartSettings, updateBarChartSettings, rebuildBarChartSettings}  from './visualizations/bar/BarChartSetup';
import {getTreemapChartSettings, updateTreemapChartSettings, rebuildTreemapChartSettings}  from './visualizations/treemap/TreemapChartSetup';
import {getSankeyChartSettings, updateSankeyChartSettings, rebuildSankeyChartSettings}  from './visualizations/sankey/SankeyChartSetup';
import {getTableSettings, updateTableSettings, rebuildTableSettings}  from './visualizations/table/GridTableSetup';
import {getMapSettings, updateMapSettings, rebuildMapSettings}  from './visualizations/map/MapSetup';

//properties set by parent
// const props = defineProps({
// 	showSkeletonLoader: {
// 		type: Boolean,
// 		default: false,
// 		required: true
// 	}
// })

//componenet methods exposed to parent
defineExpose({
	setupVis,
	updateChartType,
	applyFilter,
	showNoDataMsg, 
	toggleSkeletonLoader
});

//events raised by component
const emit = defineEmits(["setChartObj", "setMapObj", "openFiltersPanel", "clearError", "raiseError", "refreshFilter"])

//reactive field proxies
const chartProps = ref({});
const chartType = ref({})
const chartSettings = ref({})
const categories = ref([])
const indicator = ref({})

const { userInputState } = inject('userInputState') //holds state of has year, geo been set by user

//Technique for forcing Highcharts to destroy existing chart and create a new chart instead of updating existing chart
//Changing chart :key property triggers HC to create new chart instance
const chartKey = ref(0)

//binding to invoke method on child component
const childRefHighcharts = ref()
const childRefGridTable = ref({})
const childRefMap = ref({})
const childRefNoDataMsg = ref({})
const childRefSettingsSource = ref({})


//defineModel makes a two-way binding
const showNoDataWarning = defineModel({	type: Boolean,	default: false });
const showSkeletonLoader = ref(false); //defineModel({	type: Boolean,	default: false });

//local variable
let chartObj = null
let categoryObj = null

//methods
watch(childRefHighcharts, (newValue) => {
	if(newValue != null && newValue.chart != null && newValue.chart.series.length > 0) {
		chartObj = newValue.chart
		emit('setChartObj', chartObj)
	}
})

function openFilters() {
	emit('openFiltersPanel')
}

function setMapObj(mapObj) {
	emit('setMapObj', mapObj)
}

function openFootnoteModal(e) {
	const tab = e?.target.attributes["data-value"]?.value||"settings"
	childRefSettingsSource.value.openModal(tab)
}

function gotoDataNotes() {
	// footnote link onClick event fires a click event on the Data Notes menu item
	const menuItems = document.querySelector('.viz-menu-container').querySelectorAll('.menu-item')
	if(menuItems != null) {
		menuItems.forEach(item => {
			if (item.innerText.trim() == 'Data Notes') {
				item.click()
			}
		})
	}  
}

const onChartInit = (chartInstance) => {
	console.log('onChartInit')
	// adding an eventListener to "See full data notes" link
	const footerLinkElement = chartInstance.container.querySelector('.explorer-footnote-link')
	if (footerLinkElement) {
		footerLinkElement.addEventListener('click', () => {
			gotoDataNotes() 
		})
	}
}

async function setupVis(type, ind, settings, req) {
	indicator.value = ind
	chartType.value = type

	if(type == ChartTypeEnum.SOURCE.chartName){
		chartSettings.value = settings
		return chartSettings.value
	}
	showNoDataWarning.value = false
	//showSkeletonLoader.value = true
	//toggleSkeletonLoader(true)
	try {
		const cols = []
		ind.columns.items.forEach(col => {
			if(col.categoryLabelFlag) {
				//Parent in hierarchies should not be a category when it does not have subTotals - this causes API error
				//if(col.childColumnName == null || (col.subtotalValues != null && col.subtotalValues.length > 0)) {
				if(col.childColumnName == null) {
					cols.push(col)
				}
			}
		})
		categories.value = cols;
		//categories.value = ind.columns.items		
		chartSettings.value = await getChartSettings(chartType.value, indicator.value, settings, req)
		switch (type) {
			case ChartTypeEnum.LINE.chartName:
				chartProps.value = await defineLineChartProps(type, indicator.value, chartSettings.value, { onMoreClick: openFootnoteModal })
				chartKey.value++ // forces Vue to re-create the component
				break;
			case ChartTypeEnum.BAR.chartName:
				chartProps.value = await defineBarChartProps(type, indicator.value, chartSettings.value, { onMoreClick: openFootnoteModal })
				chartKey.value++ // forces Vue to re-create the component
				break
			case ChartTypeEnum.BARH.chartName:
				chartProps.value = await defineBarChartProps(type, indicator.value, chartSettings.value, { onMoreClick: openFootnoteModal })
				chartKey.value++ // forces Vue to re-create the component
				break;
			case ChartTypeEnum.TREEMAP.chartName:
				chartProps.value = await defineTreemapChartProps(type, indicator.value, chartSettings.value, { onMoreClick: openFootnoteModal })
				chartKey.value++ // forces Vue to re-create the component
				break;
			case ChartTypeEnum.SANKEY.chartName:
				chartProps.value = await defineSankeyChartProps(type, indicator.value, chartSettings.value, { onMoreClick: openFootnoteModal })
				chartKey.value++ // forces Vue to re-create the component
				break;
			case ChartTypeEnum.TABLE.chartName:
				await childRefGridTable.value.setupTable(indicator.value, chartSettings.value, { onMoreClick: openFootnoteModal })
				break;
			case ChartTypeEnum.MAP.chartName:
				await childRefMap.value.setupMap(indicator.value, chartSettings.value)
				break;
			// case ChartTypeEnum.SOURCE.chartName:
			// 	//chartProps.value = await setIndicatorSourceNotes(indicator.value, type, req)
			// 	chartKey.value++ // forces Vue to re-create the component
			// 	break;
			default:
				chartProps.value = await defineLineChartProps(type, indicator.value, chartSettings.value)
				chartKey.value++ // forces Vue to re-create the component
		}
	} catch(err) {
		//display warning instead of table
		if(err.message == "No records found") {
			showNoDataMsg()
			return {}
		} else {
			console.log(`Error building ${type} chart type`, err)
			//chartSettings.value = {};
			throw err
		}
	}
	//showSkeletonLoader.value = false
	//toggleSkeletonLoader(false)
	if(import.meta.env.VITE_APP_DEBUG)
		console.log('chartProps', chartProps.value)
	return chartSettings.value || {}
}

async function applyFilter(ind, filteredDim) {
	let options;
	showNoDataWarning.value = false
	//showSkeletonLoader.value = true

	await toggleSkeletonLoader(true)
	indicator.value = ind
	try {
		switch (chartType.value) {
			case ChartTypeEnum.LINE.chartName:
				options = await refreshLineChart(chartType.value, chartObj, indicator.value, chartSettings.value, filteredDim, { onMoreClick: openFootnoteModal })
				if(options != null) {
					chartProps.value = options
					chartKey.value++ // forces Vue to re-create the component
				}
				break;
			case ChartTypeEnum.BAR.chartName:
				options = await refreshBarChart(chartType.value, chartObj, indicator.value, chartSettings.value, filteredDim, { onMoreClick: openFootnoteModal })
				if(options != null) {
					chartProps.value = options
					chartKey.value++ // forces Vue to re-create the component
				}
				break;
			case ChartTypeEnum.BARH.chartName:
				options = await refreshBarChart(chartType.value, chartObj, indicator.value, chartSettings.value, filteredDim, { onMoreClick: openFootnoteModal })
				if(options != null) {
					chartProps.value = options
					chartKey.value++ // forces Vue to re-create the component
				}
				break;
			case ChartTypeEnum.TREEMAP.chartName:
				options = await refreshTreemapChart(chartType.value, chartObj, indicator.value, chartSettings.value, filteredDim, { onMoreClick: openFootnoteModal })
				if(options != null) {
					chartProps.value = options
					chartKey.value++ // forces Vue to re-create the component
				}
				break;
			case ChartTypeEnum.SANKEY.chartName:
				options = await refreshSankeyChart(chartType.value, chartObj, indicator.value, chartSettings.value, filteredDim, { onMoreClick: openFootnoteModal })
				if(options != null) {
					chartProps.value = options
					chartKey.value++ // forces Vue to re-create the component
				}
				break;
			case ChartTypeEnum.MAP.chartName:
				await childRefMap.value.refreshMap(indicator.value, chartSettings.value, filteredDim)
				break;
			case ChartTypeEnum.TABLE.chartName:
				await childRefGridTable.value.setupTable(indicator.value, chartSettings.value, { onMoreClick: openFootnoteModal })
				break;
			// case ChartTypeEnum.SOURCE.chartName:
			// 	//chartProps.value = await setIndicatorSourceNotes(indicator.value, type, req)
			// 	chartKey.value++ // forces Vue to re-create the component
			// 	break;
			default:
				options = refreshLineChart(chartType.value, chartObj, indicator.value, categoryObj, filteredDim, { onMoreClick: openFootnoteModal })
				if(options != null) {
					chartProps.value = options
					chartKey.value++ // forces Vue to re-create the component
				}
		}
		//showSkeletonLoader.value = false
	} catch(err) {
		//display warning instead of table
		if(err.message == "No records found") {
			showNoDataMsg()
			return
		} else {
			console.log(`Error building ${chartType.value} chart type`, err)
			//chartSettings.value = {};
			throw err
		}
	} finally {
		await toggleSkeletonLoader(false)
	}
}

async function getChartSettings(type, indicator, settings, req) {
	switch (type) {
		case ChartTypeEnum.LINE.chartName:
			if(req !== undefined) {
				return rebuildLineChartSettings(indicator, type, req)
			} else if(settings !== undefined){
				return updateLineChartSettings(settings, indicator, type, userInputState.value)
			} else {
				return getLineChartSettings(indicator, type, userInputState.value)
			}
		case  ChartTypeEnum.BAR.chartName:
			if(req !== undefined) {
				return rebuildBarChartSettings(indicator, type, req)
			} else if(settings !== undefined){
				return updateBarChartSettings(settings, indicator, type, userInputState.value)
			} else {
				return getBarChartSettings(indicator, type, userInputState.value)
			}
		case  ChartTypeEnum.BARH.chartName:
			if(req !== undefined) {
				return rebuildBarChartSettings(indicator, type, req)
			} else if(settings !== undefined){
				return updateBarChartSettings(settings, indicator, type, userInputState.value)
			} else {
				return getBarChartSettings(indicator, type, userInputState.value)
			}
		case  ChartTypeEnum.TREEMAP.chartName:
			if(req !== undefined) {
				return rebuildTreemapChartSettings(indicator, type, req)
			} else if(settings !== undefined) { // && settings.type == type
				return updateTreemapChartSettings(settings, indicator, type)
				//return settings; //updateTreemapChartSettings(settings, indicator, type)
			} else {
				return getTreemapChartSettings(indicator, type)
			}
		case  ChartTypeEnum.SANKEY.chartName:
			if(req !== undefined) {
				return rebuildSankeyChartSettings(indicator, type, req)
			} else if(settings !== undefined) { // && settings.type == type){
				return updateSankeyChartSettings(settings, indicator, type)
				//return settings; //updateSankeyChartSettings(settings, indicator, type)
			} else {
				return getSankeyChartSettings(indicator, type)
			}
		case  ChartTypeEnum.TABLE.chartName:
			if(req !== undefined) {
				return rebuildTableSettings(indicator,type, req)
			} else if(settings !== undefined){
				return updateTableSettings(settings, indicator, type, userInputState.value)
			} else {
				return getTableSettings(indicator, type, userInputState.value)
			}
		case  ChartTypeEnum.MAP.chartName:
			if(req !== undefined) {
				return await rebuildMapSettings(indicator, type, req)
			} else if(settings !== undefined){
				return updateMapSettings(settings, indicator, userInputState.value)
			} else {
				return getMapSettings(indicator, settings, userInputState.value)
			}
		// case  ChartTypeEnum.SOURCE.chartName:
		// 	if(req !== undefined) {
		// 		return setIndicatorSourceNotes(indicator, ChartTypeEnum.SOURCE.chartName, req)
		// 	} else {
		// 		return setIndicatorSourceNotes(indicator, ChartTypeEnum.SOURCE.chartName, null)
		// 	}
		// 	return
		default:
			return getLineChartSettings(indicator, ChartTypeEnum.LINE.chartName, userInputState.value)
	}
}

async function updateChartType(type, ind, settings) {
	emit("clearError")

	await toggleSkeletonLoader(true)
	try {
		settings = await setupVis(type, ind, settings).catch (err => {
			chartType.value = type
			console.log('Error updatChartType', err)
			throw err
		})
		if(import.meta.env.VITE_APP_DEBUG)
			console.log('indicator', indicator.value)		
		return settings
	} catch (err) {
		console.log( 'Error updateChartType setupVis',err)
		emit('raiseError', "Error updating chart type")
	} finally {
		await toggleSkeletonLoader(false)
	}	
}

async function updateBarChartType (type, options) {
	emit("clearError")

	await toggleSkeletonLoader(true)
	chartType.value = type
	chartSettings.value.toggleStack = options.toggleStack
	chartSettings.value.toggleHorizontalBar = options.toggleHorizontalBar
	try {
		await setupVis(type, indicator.value, chartSettings.value)
		if(import.meta.env.VITE_APP_DEBUG)
			console.log('indicator', indicator.value)			
		emit("refreshFilter", chartSettings.value)
	} catch (err) {
		console.log( 'Error updateBarChartType',err)
		emit('raiseError', "Error updating bar chart direction")
	} finally {
		await toggleSkeletonLoader(false)
	}
}

async function updateCategory(settings) {
	emit("clearError")

	await toggleSkeletonLoader(true)
	chartSettings.value = settings
	try {
		await setupVis(chartType.value, indicator.value, chartSettings.value)
		if(import.meta.env.VITE_APP_DEBUG)
			console.log('indicator', indicator.value)			
		emit("refreshFilter", chartSettings.value)
	} catch (err) {
		console.log( 'Error updateCategory setupVis',err)
		emit('raiseError', "Error updating category")
	} finally {
		await toggleSkeletonLoader(false)
	}
}

function toggleMapLabels(value) {
	childRefMap.value.toggleMapDataLabels(value);
}

function showNoDataMsg(){
	showNoDataWarning.value = true
	childRefNoDataMsg.value.setupMsg(indicator.value)
}

async function toggleSkeletonLoader(value) {
	showSkeletonLoader.value = value 
	await nextTick()
	await new Promise(requestAnimationFrame);
}
</script>

<template>
 	<div v-if="chartType !== 'table'" class="chart-setup">
		<div v-show="!showNoDataWarning" ><!-- && !showSkeletonLoader" -->
			<div v-show="!showSkeletonLoader" >
				<div class="chart-setup-row">
					<div v-if="chartType == 'line'" style="width:100%">
						<LineChartOptions :indicator="indicator" :categories="categories"
						:chartSettings="chartSettings" @openFilters="openFilters" @updateCategory="updateCategory"  />
					</div>

					<div v-if="(chartType == 'column' || chartType == 'bar')" style="width:100%">
						<BarChartOptions :selectedChartType="chartType" :indicator="indicator"
							:chartSettings="chartSettings" :categories="categories" @openFilters="openFilters"
							@updateBarChartType="updateBarChartType" @updateChartCategory="updateCategory"/>
					</div>

					<div v-if="chartType == 'treemap'" style="width:100%">
						<TreemapChartOptions :indicator="indicator" :categories="categories"
						:chartSettings="chartSettings" @openFilters="openFilters" @updateChartCategory="updateCategory"  />
					</div>

					<div v-if="chartType == 'sankey'" style="width:100%">
						<SankeyChartOptions :indicator="indicator" :categories="categories"
						:chartSettings="chartSettings" @openFilters="openFilters" @updateChartCategory="updateCategory"  />
					</div>

					<div v-if="chartType == 'map'" style="width:100%">
						<MapOptions @openFilters="openFilters" @updateCategory="updateCategory" @toggleMapLabels="toggleMapLabels" />
					</div>
				</div>
			</div>
		</div>
	</div>
	<div v-show="!showNoDataWarning" class="flexible">
		<div v-show="!showSkeletonLoader" class="flexible"><!-- !showSkeletonLoader" -->
			<div class="chart-padding chart-wrapper" v-if="chartType == 'line' || chartType == 'column' || chartType == 'bar' || chartType == 'treemap' || chartType == 'sankey'">
				<highcharts id="hctag" 
						class="highcharts-light" 
						:key="chartKey" 
						ref="childRefHighcharts" 
						:callback="onChartInit" 
						v-model:options="chartProps"
						></highcharts>
			</div>
			<div class="chart-padding chart-wrapper" v-if="chartType == 'map'">
				<MapComponent ref="childRefMap" @setMapObj="setMapObj" @openFootnoteModal="openFootnoteModal" @toggleSkeletonLoader="toggleSkeletonLoader"></MapComponent>
			</div>
			<div class="chart-padding chart-wrapper" v-if="chartType == 'table'">
				<GridTable ref="childRefGridTable" @openFilters="openFilters" ></GridTable>
			</div>			
		</div>
	 	<div v-show="showSkeletonLoader" class="chart-padding no-data-warning">
			<SkeletonLoader  />
		</div>	
	</div>
	<div v-show="showNoDataWarning" class="chart-padding no-data-warning">
		<NoDataMsg :indicator="indicator" ref="childRefNoDataMsg" />
	</div>	
	<div class="chart-padding" v-if="chartType == 'source'">
		<SourceComponent class="highcharts-light" ref="childRefSource" :indicator="indicator"></SourceComponent>
	</div>
	<SettingsSource ref="childRefSettingsSource" :source="chartSettings.srcHtml" :abbreviations="chartSettings.keyHtml" />
</template>

<style >
.chart-padding {
	padding: 0 1.25rem;
	
}
.chart-wrapper {
	min-height: 60vh;
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	flex-shrink: 1;
  	min-width: 0;      /* Crucial for row layouts to allow shrinking */
  	/*min-height: 0;     /* Crucial for column layouts to allow shrinking */
  	overflow: hidden;  /* Collapses constraints, forcing Highcharts to adapt */
}
.highcharts-tooltip .explorer-tool-tip div {
	color: var(--text-Header, #171717);
	z-index: 9999;
	/* Regular/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-chart-Tooltip);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base); /* 150% */
	background-color:#FFFFFF;
}

.highcharts-tooltip .explorer-tool-tip .header div {
	font-weight: 700;
	font-size: var(--font-size-chart-Tooltip);
}

.highcharts-tooltip .explorer-tool-tip .header .title {
	font-size: var(--font-size-H6);
	font-weight: 700;	
}

.highcharts-tooltip .explorer-tool-tip-list {
	font-size: var(--font-size-chart-Tooltip);
    list-style-type: none;
    padding: 0px;
    margin: 0px;
    overflow: hidden;
}

.explorer-footnote-wrapper {
	padding-right: .5rem;
}

.explorer-footnote {
	color: var(--text-Secondary, #757575);

	/* Regular/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base); /* 150% */
}
.explorer-footnote .title {
	color: var(--text-Primary, #2C2C2C);
	font-weight: 500;
	/*padding: 0.5rem;*/
}
.explorer-footnote-link {
	color: var(--text-Link);
	font-weight: 500;
	padding: 0em 0em .125em 0em;
	height: unset;
	min-height: unset;
	cursor: pointer;
}

.table-footnote {
	margin-bottom: 1rem;
}

.highcharts-caption {
	width: 100%;
}
.highcharts-caption .footnote-more,
.table-footnote .footnote-more {
    display: none;
}

.chart-wrapper, .tbl-container {
	container-type: inline-size;
}
#hctag {
	display: flex;
	flex-direction: column;
  	flex-grow: 1;
  	min-width: 0;      /* Crucial for row layouts to allow shrinking */
  	min-height: 0;     /* Crucial for column layouts to allow shrinking */
  	overflow: hidden;  /* Collapses constraints, forcing Highcharts to adapt */ 
}
@container (max-width: 965px) {
	.explorer-footnote-wrapper {
		text-wrap: nowrap;
	}

	.highcharts-caption .explorer-footnote,
	.table-footnote .explorer-footnote {
		text-wrap: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: inline-block;
        width: 80%;
	}

	.highcharts-caption .footnote-more,
	.table-footnote .footnote-more {
		display: inline;
		margin-left: .75rem;
        padding: 0;
        vertical-align: top;
        height: 1.5rem;
		min-width: 2rem;
		min-height: unset;
		text-align: start;
	}
}

@container (max-width: 499px) {
	.highcharts-caption .explorer-footnote,
	.table-footnote .explorer-footnote {
        width: 70%;
	}
}
</style>
